import React from "react";
import { makeStyles } from "@material-ui/core/styles";

import { T } from "../../constants";
import { useUi, cx } from "../../ui";
import { ARRIVAL } from "../../arrival";
import { dateLabel, whenText } from "../../dayTime";
import { fromLocalInput, toLocalInput } from "../../departure";
import DayTimePicker from "../../components/DayTimePicker";
import FieldRow from "../../components/FieldRow";

const useStyles = makeStyles({
  note: { margin: 0, fontSize: 12, color: T.mutedFg },
  danger: { margin: "4px 0 0", fontSize: 11, color: T.danger },
});

/**
 * When the guest expects to turn up, and the day the booking is sold on.
 *
 * Every card that lets an arrival be picked now insists on one - the tiles that
 * used to offer the card's own entry time are gone. What is left is the picker
 * itself, and, for the handful of cards that fix their own entry, the date the
 * booking is sold on.
 *
 * `locked` reads it back instead: an arrival the guest has already made is a
 * record of what happened, not a plan anyone can still move.
 */
export default function ArrivalSection({
  arrival,
  isFixedPackage,
  showDates,
  startDay,
  tz,
  now,
  minIso,
  allowPast,
  locked,
}) {
  const s = useStyles();
  const ui = useUi();

  const { mode, at, setAt, setDay, scheduledEntryLabel, outOfRange } = arrival;

  if (locked) {
    return (
      <FieldRow label="Arrives" hint="The guest has already arrived.">
        <p className={s.note}>
          {mode === ARRIVAL.SCHEDULED
            ? whenText({ iso: fromLocalInput(at, tz), tz, now })
            : dateLabel({ day: startDay, tz })}
        </p>
      </FieldRow>
    );
  }

  /* The guest is at the desk; the server stamps the moment we save. */
  if (mode === ARRIVAL.NOW) return null;

  if (mode === ARRIVAL.SCHEDULED) {
    /* The hook holds venue wall-clock; the picker deals in instants. */
    const iso = fromLocalInput(at, tz);

    return (
      <FieldRow label="Arrives" hint={whenText({ iso, tz, now })}>
        <DayTimePicker
          tz={tz}
          now={now}
          label="Arrives"
          value={iso}
          onChange={(next) => setAt(toLocalInput(next, tz))}
          minIso={minIso}
          allowPast={allowPast}
          minReason="In the past"
        />
        {outOfRange && (
          <p className={s.danger}>That is outside this booking's window.</p>
        )}
      </FieldRow>
    );
  }

  return (
    <FieldRow
      label={isFixedPackage ? "Scheduled entry (fixed)" : "Scheduled entry"}
      htmlFor="qb-entry-day"
      hint={
        scheduledEntryLabel
          ? `Set by this guest card — from ${scheduledEntryLabel}.`
          : "Set by this guest card."
      }
    >
      {showDates ? (
        <input
          id="qb-entry-day"
          type="date"
          value={startDay}
          disabled={isFixedPackage}
          onChange={(e) => setDay(e.target.value)}
          className={cx(ui.field)}
        />
      ) : (
        <p className={s.note}>The guest card sets when this booking opens.</p>
      )}
    </FieldRow>
  );
}
